import * as React from "react";
import {
  Modal,
  Table,
  Tabs,
  Tag,
  Typography,
  Spin,
  Descriptions,
  Row,
  Col,
  Statistic,
  Card,
  Pagination,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import useSWR from "swr";
import dayjs from "dayjs";
import {
  getCampaignStatisticsApi,
  getCampaignParticipationsApi,
} from "src/api/campaign/campaignApi";
import type { CampaignParticipation, CampaignStatistics } from "src/types/campaign";
import RewardIssueFailedModal from "src/components/campaign/RewardIssueFailedModal";

const PARTICIPATION_PAGE_SIZE = 20;

type Props = {
  open: boolean;
  campaignId: number;
  onClose: () => void;
};

const triggerTypeLabel: Record<string, string> = {
  AUTO: "자동 발급",
  MANUAL: "수동 발급",
  CLAIM: "직접 수령",
  EVENT_ATTENDANCE: "출석체크",
  EVENT_RANDOM: "확률 기반",
};

type RuleStatRow = CampaignStatistics["ruleStatistics"][number];

export default function CampaignLogsModal({ open, campaignId, onClose }: Props) {
  const [failedModalOpen, setFailedModalOpen] = React.useState(false);
  const [participationPage, setParticipationPage] = React.useState(1);

  const swrKey = React.useMemo(
    () => (open ? (["campaign/statistics", campaignId] as const) : null),
    [open, campaignId]
  );

  const { data, isLoading } = useSWR(swrKey, async () => getCampaignStatisticsApi(campaignId), {
    revalidateOnFocus: false,
  });

  const participationKey = React.useMemo(
    () => (open ? (["campaign/participations", campaignId, participationPage] as const) : null),
    [open, campaignId, participationPage]
  );

  const { data: participationData, isLoading: participationLoading } = useSWR(
    participationKey,
    async () =>
      getCampaignParticipationsApi({
        campaignId,
        page: Math.max(0, participationPage - 1),
        size: PARTICIPATION_PAGE_SIZE,
      }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  React.useEffect(() => {
    if (open) setParticipationPage(1);
  }, [open]);

  const stats = data?.data;
  const participations = participationData?.data?.content ?? [];
  const participationTotal = participationData?.data?.page?.totalElements ?? 0;

  const participationColumns: ColumnsType<CampaignParticipation> = [
    { title: "참여 ID", dataIndex: "participationId", width: 90 },
    { title: "사용자 ID", dataIndex: "userId", width: 90 },
    { title: "이름", dataIndex: "username", width: 110 },
    { title: "이메일", dataIndex: "email", width: 180 },
    {
      title: "상태",
      dataIndex: "status",
      width: 90,
      render: (value: string) => (
        <Tag color={value === "SUCCESS" ? "success" : "error"}>{value}</Tag>
      ),
    },
    {
      title: "선택된 규칙",
      dataIndex: "selectedRule",
      width: 160,
      render: (value: CampaignParticipation["selectedRule"]) =>
        value ? (
          value.isNone ? (
            <Tag color="default">꽝</Tag>
          ) : (
            value.issueName || `#${value.ruleId}`
          )
        ) : (
          "-"
        ),
    },
    {
      title: "리워드",
      dataIndex: "rewardIssue",
      width: 160,
      render: (value: CampaignParticipation["rewardIssue"]) => value?.rewardName || "-",
    },
    {
      title: "발급 상태",
      dataIndex: "rewardIssue",
      width: 110,
      render: (value: CampaignParticipation["rewardIssue"]) =>
        value?.issueStatus ? <Tag>{value.issueStatus}</Tag> : "-",
    },
    {
      title: "출석일",
      dataIndex: "attendDate",
      width: 110,
      render: (value: string) => value || "-",
    },
    {
      title: "참여일시",
      dataIndex: "participatedAt",
      width: 160,
      render: (value: string) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
  ];

  const ruleColumns: ColumnsType<RuleStatRow> = [
    { title: "Rule ID", dataIndex: "ruleId", width: 80 },
    {
      title: "트리거 타입",
      dataIndex: "triggerType",
      width: 120,
      render: (value: string) => triggerTypeLabel[value] || value || "-",
    },
    { title: "조건 타입", dataIndex: "conditionType", width: 140 },
    { title: "발급명", dataIndex: "issueName", width: 160 },
    {
      title: "리워드명",
      dataIndex: "rewardName",
      width: 160,
      render: (value, record) => (record.isNone ? <Tag color="default">없음</Tag> : value || "-"),
    },
    { title: "발급 수", dataIndex: "issueCount", width: 90 },
  ];

  const statisticsTab = (
    <>
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <Spin />
        </div>
      ) : stats ? (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 20 }}>
            <Descriptions.Item label="캠페인명" span={2}>
              <Typography.Text strong>{stats.campaignName}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="트리거 타입">
              {triggerTypeLabel[stats.triggerType] || stats.triggerType}
            </Descriptions.Item>
            <Descriptions.Item label="기간">
              {stats.period.startDate} ~ {stats.period.endDate}
            </Descriptions.Item>
          </Descriptions>

          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <Card size="small" title="참여 통계">
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="총 참여수" value={stats.participation.totalCount} />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="성공"
                      value={stats.participation.successCount}
                      valueStyle={{ color: "#3f8600" }}
                    />
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Statistic title="고유 참여자" value={stats.participation.uniqueUserCount} />
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Statistic
                      title="실패"
                      value={stats.participation.failedCount}
                      valueStyle={{
                        color: stats.participation.failedCount > 0 ? "#cf1322" : undefined,
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="리워드 발급 통계">
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="총 발급수" value={stats.reward.totalIssued} />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="완료"
                      value={stats.reward.completedCount}
                      valueStyle={{ color: "#3f8600" }}
                    />
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Statistic
                      title="대기"
                      value={stats.reward.pendingCount}
                      valueStyle={{ color: "#d46b08" }}
                    />
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Statistic
                      title={
                        stats.reward.failedCount > 0 ? (
                          <Typography.Link
                            onClick={() => setFailedModalOpen(true)}
                            style={{ fontSize: 12 }}
                          >
                            실패 (목록 보기)
                          </Typography.Link>
                        ) : (
                          "실패"
                        )
                      }
                      value={stats.reward.failedCount}
                      valueStyle={{
                        color: stats.reward.failedCount > 0 ? "#cf1322" : undefined,
                        cursor: stats.reward.failedCount > 0 ? "pointer" : undefined,
                      }}
                      formatter={(val) => (
                        <span
                          onClick={() => stats.reward.failedCount > 0 && setFailedModalOpen(true)}
                        >
                          {val}
                        </span>
                      )}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            규칙별 통계
          </Typography.Title>
          <Table
            columns={ruleColumns}
            dataSource={stats.ruleStatistics}
            rowKey={(r) => String(r.ruleId)}
            pagination={false}
            size="small"
            locale={{ emptyText: "규칙 통계가 없습니다." }}
            scroll={{ x: 1000 }}
          />
        </>
      ) : (
        <Typography.Text type="secondary">데이터가 없습니다.</Typography.Text>
      )}
    </>
  );

  const participationTab = (
    <>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        총 {participationTotal.toLocaleString()}건
      </Typography.Text>
      <Table
        columns={participationColumns}
        dataSource={participations}
        rowKey={(r) => String(r.participationId)}
        pagination={false}
        loading={participationLoading}
        size="small"
        locale={{ emptyText: "참여 내역이 없습니다." }}
        scroll={{ x: 1000 }}
      />
      {participationTotal > PARTICIPATION_PAGE_SIZE && (
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
          <Pagination
            current={participationPage}
            total={participationTotal}
            pageSize={PARTICIPATION_PAGE_SIZE}
            onChange={setParticipationPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <Modal
        title={`캠페인 통계 / 참여 로그 (ID: ${campaignId})`}
        open={open}
        onCancel={onClose}
        width={1100}
        footer={null}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
      >
        <Tabs
          items={[
            { key: "statistics", label: "통계", children: statisticsTab },
            { key: "participations", label: "참여 로그", children: participationTab },
          ]}
        />
      </Modal>

      <RewardIssueFailedModal
        open={failedModalOpen}
        campaignId={campaignId}
        onClose={() => setFailedModalOpen(false)}
      />
    </>
  );
}
